/* eslint-disable @typescript-eslint/no-unused-vars */
import { Image, useTranslation } from '@packages/utils';
import { TColumnType } from 'components/Base/Table/Table.types';
import { ParagraphEllipsis } from 'components/Shared/ParagraphEllipsis';
import {
  ENotificationType,
  useShowToastNotification,
} from 'components/Base/Notification';
import { useUpdateEventAttendanceMutation } from 'redux/apiSlice/registrants.slice';
import { ANSWER_MAX_LENGTH } from 'pages/private/Workshop/components/WorkShopDetails/WaitlistTab/WorkshopWaitList/WorkshopWaitList.data';
import {
  ESortDirection,
  TRtkErrorType,
  // EWorkshopLanguage,
} from 'data/types/generic.types';
import {
  EAttendanceColumnKey,
  EAttendanceStatus,
  TAttendanceEditUI,
  TAttendanceListUI,
} from 'data/types/registrants.types';
import photographyAllowed from 'assets/images/photographyAllowed.svg';
import photographyNotAllowed from 'assets/images/photographyNotAllowed.svg';
import useGetTableColumnData from 'components/Base/Table/components/SearchFilter';
import {
  EEventSeriesType,
  EEventStandaloneType,
  EEventStatus,
} from 'data/types/events.types';
import ButtonGroup from 'components/Base/ButtonGroup/ButtonGroup';
import { Switch } from 'components/Base/Switch';
import { useAppSelector } from 'redux/store';
import { useMemo } from 'react';
import { EUserType } from 'data/types/user.types';
import { useCheckPermission } from 'hooks/useCheckPermission';
import dayjs from 'dayjs';

const ADMIN_TYPES = [
  EUserType.READY_ADMIN,
  EUserType.SUPER_ADMIN,
  EUserType.ELC_LEAD,
  EUserType.CAFE_COORDINATOR,
  EUserType.ELC_PM,
];

const useGetData = ({
  eventId,
  eventStatus,
  sessionDateTime,
  onNotificationToggle,
}: {
  eventId: string;
  eventStatus?: EEventStatus;
  eventType?: EEventStandaloneType | EEventSeriesType;
  sessionDateTime?: string;
  onNotificationToggle?: (record: TAttendanceListUI, checked: boolean) => void;
}) => {
  const usersData = useAppSelector(state => state.userSlice);
  const { user } = usersData;

  const isAdmin = useMemo(() => {
    return user?.type?.some((type: EUserType) => ADMIN_TYPES.includes(type));
  }, [user]);

  const { t } = useTranslation();

  const { getTableColumnSearchProps } = useGetTableColumnData();
  const defaultSortParam = {
    sortField: EAttendanceColumnKey.LAST_NAME,
    sortDirection: ESortDirection.descend,
  };

  const { showToastNotification } = useShowToastNotification();

  const [updateAttendance] = useUpdateEventAttendanceMutation();

  const hasNotificationPermission = useCheckPermission(
    'EVENT_REGISTRANTS_EDIT_BASIC_INFO',
  );
  const canEditNotifications = hasNotificationPermission || isAdmin;

  const isNotificationDisabled = useMemo(() => {
    if (
      eventStatus === EEventStatus.CLOSED ||
      eventStatus === EEventStatus.CANCELLED
    ) {
      return true;
    }
    if (sessionDateTime && dayjs().isAfter(dayjs(sessionDateTime))) {
      return true;
    }
    return false;
  }, [eventStatus, sessionDateTime]);

  const isUpdatable = (record: TAttendanceListUI): boolean => {
    const currentAttendance = record.attendances.find(
      attendance =>
        attendance.partAttendanceId === record.currentAttendanceSessionId,
    );

    return currentAttendance?.isUpdatable || false;
  };

  const isDisabled = (record: TAttendanceListUI): boolean => {
    return (
      !isUpdatable(record) ||
      eventStatus === EEventStatus.CLOSED ||
      eventStatus === EEventStatus.CANCELLED
    );
  };

  const handleSubmit = async ({
    registrantId,
    attendances,
    orientationKitReceived,
  }: {
    registrantId: string;
    attendances?: TAttendanceEditUI[];
    orientationKitReceived: boolean;
  }) => {
    try {
      await updateAttendance({
        attendances: attendances || [],
        registrantId,
        eventId,
        orientationKitReceived,
      }).unwrap();
      showToastNotification({
        message: t(
          'descriptions.admin-portal.events.details.event-registrants.actions.edit.modal.SUCCESS_MESSAGE',
        ),
        type: ENotificationType.SUCCESS,
      });
    } catch (err) {
      showToastNotification({
        message: (err as TRtkErrorType)?.data?.message,
        type: ENotificationType.ERROR,
      });
    }
  };

  const handleAttendanceUpdate = async (
    record: TAttendanceListUI,
    updates: {
      attendanceStatus?: EAttendanceStatus;
      kitCount?: number;
      orientationKitReceived?: boolean;
    },
  ) => {
    const currentAttendance = record.attendances.find(
      attendance =>
        attendance.partAttendanceId === record.currentAttendanceSessionId,
    );

    if (!currentAttendance) return;

    const updatedAttendance = {
      attendanceId: currentAttendance.partAttendanceId,
      attendanceStatus:
        updates.attendanceStatus || currentAttendance.attendanceStatus,
      kitCount: updates.kitCount ?? currentAttendance.kitCount ?? 0,
      sessions: record.sessions || [],
    };

    await handleSubmit({
      registrantId: record.registrantId,
      attendances: [updatedAttendance],
      orientationKitReceived:
        updates.orientationKitReceived ?? record.binders.received,
    });
  };

  // const materialsLanguageFilterData = [
  //   {
  //     text: 'Eng',
  //     value: EWorkshopLanguage.ENGLISH,
  //   },
  //   {
  //     text: 'Spa',
  //     value: EWorkshopLanguage.SPANISH,
  //   },
  // ];

  const defaultColumns: TColumnType<TAttendanceListUI>[] = [
    {
      title: t(
        'descriptions.admin-portal.events.details.attendance-list.table.title.FIRST_NAME',
      ),
      dataIndex: 'firstName',
      key: EAttendanceColumnKey.FIRST_NAME,
      sorter: true,
      ...getTableColumnSearchProps<TAttendanceListUI>(),
    },
    {
      title: t(
        'descriptions.admin-portal.events.details.attendance-list.table.title.LAST_NAME',
      ),
      dataIndex: 'lastName',
      key: EAttendanceColumnKey.LAST_NAME,
      sorter: true,
      ...getTableColumnSearchProps<TAttendanceListUI>(),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: EAttendanceColumnKey.EMAIL,
      sorter: true,
      ...getTableColumnSearchProps<TAttendanceListUI>(),
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: EAttendanceColumnKey.PHONE,
      sorter: true,
    },
    {
      title: t(
        'descriptions.admin-portal.events.details.attendance-list.table.title.PHOTO',
      ),
      dataIndex: 'photo',
      key: EAttendanceColumnKey.PHOTO,
      sorter: true,
      render: (_, record) => (
        <div className="pl-2">
          {record.photo ? (
            <Image src={photographyAllowed} height={24} alt="" />
          ) : (
            <Image src={photographyNotAllowed} height={24} alt="" />
          )}
        </div>
      ),
    },
    {
      title: t(
        'descriptions.admin-portal.events.details.attendance-list.table.title.QUESTION_ANSWER',
      ),
      dataIndex: 'customQuestionAnswer',
      key: 'customQuestionAnswer',
      render: answer => (
        <ParagraphEllipsis
          text={answer}
          textMaxLength={ANSWER_MAX_LENGTH}
          fontFamily="Montserrat"
        />
      ),
      onCell: () => ({
        style: { minWidth: '250px' },
      }),
    },
    {
      title: t(
        'descriptions.admin-portal.events.details.attendance-list.table.title.ATTENDANCE',
      ),
      dataIndex: 'attendance',
      key: 'attendance',
      width: 220,
      render: (_, record) => (
        <ButtonGroup
          options={[
            {
              label: 'Attended',
              value: EAttendanceStatus.ATTENDED,
            },
            {
              label: 'Absent',
              value: EAttendanceStatus.ABSENT,
            },
          ]}
          value={record.attendance || EAttendanceStatus.ATTENDED}
          size="small"
          disabled={isDisabled(record)}
          onChange={async value => {
            if (record.attendance === value) return;
            await handleAttendanceUpdate(record, {
              attendanceStatus: value as EAttendanceStatus,
              kitCount: value === EAttendanceStatus.ABSENT ? 0 : record.kits,
              orientationKitReceived:
                value === EAttendanceStatus.ABSENT
                  ? false
                  : record.binders.received,
            });
          }}
        />
      ),
    },
    {
      title: 'Notifications',
      dataIndex: 'notificationsEnabled',
      key: EAttendanceColumnKey.NOTIFICATIONS,
      render: (_, record) => (
        <Switch
          checkedChildren="On"
          unCheckedChildren="Off"
          checked={record.notificationsEnabled !== false}
          disabled={
            !canEditNotifications ||
            isNotificationDisabled ||
            !!record.minimalRegistration
          }
          onChange={checked => {
            onNotificationToggle?.(record, checked);
          }}
        />
      ),
    },
    {
      title: ' ',
      dataIndex: 'actions',
      key: 'actions',
      fixed: 'right',
      width: 50,
      render: () => null,
    },
  ];

  return { defaultColumns, defaultSortParam };
};

export default useGetData;
